import React, { useState, useEffect, useMemo } from 'react';
import { getTiposSocia } from '../services/tiposSociaService';
import { useNavigate } from 'react-router-dom';
import tratamientoService from '../services/tratamientoService';
import profesionService from '../services/profesionService';
import mentorService from '../services/mentorService';
import hospitalService from '../services/hospitalService';
import axios from 'axios';
import config from '../config/api';
import '../styles/Dashboard.css';
import sociasApiService from '../services/sociasApiService';
import { getCurrentUserPermissions, hasPermission } from '../utils/permissions';

const Dashboard = () => {
  const navigate = useNavigate();

  // Estadísticas reales de profesiones (desde API)
  const [profesionesStats, setProfesionesStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Estados para estadísticas de tratamientos
  const [tratamientosStats, setTratamientosStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Cargar estadísticas de tratamientos
  useEffect(() => {
    const loadTratamientosStats = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Tratamientos') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const response = await tratamientoService.getAllTratamientos();
        if (response.success && response.data) {
          const tratamientos = response.data;
          const total = tratamientos.length;
          const active = tratamientos.filter(t => t.TRATAMIENTO_ESTADO === 'Activo').length;
          const inactive = tratamientos.filter(t => t.TRATAMIENTO_ESTADO === 'Finalizado').length;
          
          setTratamientosStats({
            total,
            active,
            inactive
          });
          
          // Actualizar tarjeta en dashboard
          setDashboardModules(prev => prev.map(m =>
            m.id === 'tratamientos' ? { ...m, stats: { total, active, inactive } } : m
          ));
        }
      } catch (error) {
        console.error('Error al cargar estadísticas de tratamientos:', error);
      }
    };

    loadTratamientosStats();
  }, []);

  // Cargar estadísticas de mentores
  useEffect(() => {
    const loadMentoresStats = async () => {
      try {
        const response = await mentorService.getAllMentores();
        // API devuelve { success: true, data: [...] }
        const lista = Array.isArray(response.data?.data) ? response.data.data
          : Array.isArray(response.data) ? response.data : [];
        const total = lista.length;
        const active = lista.filter(m => (m.MENTOR_ESTADO || m.mentorEstado || '').toLowerCase() === 'activo').length;
        const inactive = total - active;
        const stats = { total, active, inactive };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'mentors' ? { ...mod, stats } : mod
        ));
      } catch (error) {
        console.error('Error al cargar estadísticas de mentores:', error);
        const stats = { total: 0, active: 0, inactive: 0 };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'mentors' ? { ...mod, stats } : mod
        ));
      }
    };
    loadMentoresStats();
  }, []);

  // Cargar estadísticas de hospitales
  useEffect(() => {
    const loadHospitalesStats = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Hospitales') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const response = await hospitalService.getAllHospitales();
        const lista = Array.isArray(response.data) ? response.data : [];
        const total = lista.length;
        const stats = { total };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'hospitals' ? { ...mod, stats } : mod
        ));
      } catch (error) {
        console.error('Error al cargar estadísticas de hospitales:', error);
        const stats = { total: 0 };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'hospitals' ? { ...mod, stats } : mod
        ));
      }
    };
    loadHospitalesStats();
  }, []);

  // Cargar estadísticas de usuarios
  useEffect(() => {
    const loadUsuariosStats = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Usuarios') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const response = await axios.get(`${config.endpoints.usuarios}`);
        const lista = Array.isArray(response.data) ? response.data : (response.data?.data ? response.data.data : []);
        const total = lista.length;
        const active = lista.filter(u => (u.USUARIO_ESTADO || u.usuarioEstado || '').toLowerCase() === 'activo').length;
        const inactive = total - active;
        const stats = { total, active, inactive };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'users' ? { ...mod, stats } : mod
        ));
      } catch (error) {
        console.error('Error al cargar estadísticas de usuarios:', error);
        const stats = { total: 0, active: 0, inactive: 0 };
        
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'users' ? { ...mod, stats } : mod
        ));
      }
    };
    loadUsuariosStats();
  }, []);

    // Cargar estadísticas de médicos desde API
    useEffect(() => {
      const loadMedicosStats = async () => {
        try {
          // Verificar permiso antes de cargar
          const userPermissions = getCurrentUserPermissions();
          if (!hasPermission(userPermissions, 'CRUD Medicos') && !hasPermission(userPermissions, 'Administrador')) {
            return;
          }
          
          const data = await import('../services/medicoService').then(m => m.default.getAllMedicos());
          const lista = Array.isArray(data) ? data : [];
          const total = lista.length;
          const active = lista.filter(m => (m.MEDICO_ESTADO || '').toLowerCase() === 'activo').length;
          const inactive = total - active;
          const stats = { total, active, inactive };
          
          setDashboardModules(prev => prev.map(mod =>
            mod.id === 'medicos' ? { ...mod, stats } : mod
          ));
        } catch (error) {
          console.error('Error al cargar estadísticas de médicos:', error);
          const stats = { total: 0, active: 0, inactive: 0 };
          
          setDashboardModules(prev => prev.map(mod =>
            mod.id === 'medicos' ? { ...mod, stats } : mod
          ));
        }
      };
      loadMedicosStats();
    }, []);

  // Cargar estadísticas de profesiones desde API
  useEffect(() => {
    const loadProfesionesStats = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Profesiones') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const data = await profesionService.getAllProfesiones();
        const lista = Array.isArray(data) ? data : [];
        const total = lista.length;
  const active = lista.filter(p => (p.PROFESION_ESTADO || '').toLowerCase() === 'activo').length;
  // Si no hay activas, todas son inactivas
  const inactive = total - active;
  const stats = { total, active, inactive };
        setProfesionesStats(stats);

        // Actualizar tarjeta en dashboard
        setDashboardModules(prev => prev.map(m =>
          m.id === 'professions' ? { ...m, stats } : m
        ));
      } catch (error) {
        console.error('Error al cargar estadísticas de profesiones:', error);
        const stats = { total: 0, active: 0, inactive: 0 };
        setProfesionesStats(stats);
        setDashboardModules(prev => prev.map(m =>
          m.id === 'professions' ? { ...m, stats } : m
        ));
      }
    };
    loadProfesionesStats();
  }, []);

  // Cargar estadísticas de SOCIAS usando la misma lógica que profesiones
  useEffect(() => {
    let intervalId;
    const loadSociasStats = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Socias') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const data = await sociasApiService.getSocias();
        const lista = Array.isArray(data) ? data : [];
        const total = lista.length;
        const active = lista.filter(s => (s.sociaEstado || s.SOCIA_ESTADO || '').toLowerCase() === 'activo').length;
        const inactive = total - active;
        const stats = { total, active, inactive };
        setDashboardModules(prev => prev.map(mod =>
          mod.id === 'socias' ? { ...mod, stats } : mod
        ));
      } catch (error) {
        console.error('Error al cargar estadísticas de socias:', error);
      }
    };
    // primera carga
    loadSociasStats();
    // refresco periódico para tener "tiempo real"
    intervalId = setInterval(loadSociasStats, 10000);
    return () => intervalId && clearInterval(intervalId);
  }, []);

  // Mapeo de IDs de módulos del Dashboard a permisos requeridos
  const MODULE_PERMISSIONS = {
    'especialidades-medicas': 'CRUD Especialidad Médico',
    'requisitos-socia': 'CRUD Requisitos',
    'users': 'CRUD Usuarios',
    'professions': 'CRUD Profesiones',
    'socias': 'CRUD Socias',
    'tipo-socia': 'CRUD Tipo Socia',
    'characteristics': 'CRUD Caracteristicas',
    'mentors': 'CRUD Mentores',
    'medicos': 'CRUD Medicos',
    'hospitals': 'CRUD Hospitales',
    'inventory': 'CRUD Inventario',
    'reports': 'CRUD Reportes',
    'tratamientos': 'CRUD Tratamientos',
    'tallas': 'CRUD Talla',
    // Permiso para el módulo Editar FormatOS
    'editar-formatos': 'CRUD Formatos'
  };

  // Configuración de módulos del dashboard
  const [dashboardModules, setDashboardModules] = useState([
    {
      id: 'especialidades-medicas',
      title: 'Especialidades Médicas',
      icon: 'fas fa-notes-medical',
      hasStats: false,
      description: 'Gestiona las especialidades médicas disponibles para los médicos.',
  route: '/especialidades-medicas',
  createRoute: '/especialidades-medicas/crear',
      createButtonText: 'Registrar Especialidad',
      handler: () => navigate('/especialidades-medicas'),
      advancedCard: true // Para aplicar el borde rosa y estilos avanzados
    },
    {
      id: 'requisitos-socia',
      title: 'Requisitos de Socia',
      icon: 'fas fa-file-alt',
      hasStats: false,
      description: 'Gestiona los requisitos/documentos que la fundación solicita o recibe de las socias.',
      route: '/requisitos-socia',
      createRoute: '/requisitos-socia/crear',
      primaryButtonText: 'Gestionar',
      createButtonText: 'Agregar Requisito',
      handler: () => navigate('/requisitos-socia'),
      advancedCard: true
    },
    {
      id: 'users',
      title: 'Usuarios',
      icon: 'fas fa-users',
      hasStats: true,
      stats: { total: 0, active: 0, inactive: 0 },
      statsLabels: { active: 'Activos', inactive: 'Inactivos' },
      route: '/user-management',
      createRoute: '/create-user',
      handler: () => navigate('/user-management')
    },
    {
      id: 'professions',
      title: 'Profesiones',
      icon: 'fas fa-user-md',
      hasStats: true,
      // Inicializa en 0 y se actualizará desde la API en loadProfesionesStats
      stats: profesionesStats,
      statsLabels: { active: 'Activas', inactive: 'Inactivas' },
      statsClass: 'professions-stats',
      route: '/professions',
      createRoute: '/professions/create',
      createButtonText: 'Agregar',
      handler: () => navigate('/professions')
    },
    {
      id: 'socias',
      title: 'Socias',
      icon: 'fas fa-user-friends',
      hasStats: true,
      stats: { total: 0, active: 0, inactive: 0 },
      statsLabels: { active: 'Activas', inactive: 'Inactivas' },
      route: '/partners',
      createRoute: '/register-partner',
      description: 'Gestiona el registro y la información de las socias de FEPAST',
      createButtonText: 'Registrar Socia',
      handler: () => navigate('/partners')
    },
    {
      id: 'tipo-socia',
      title: 'Tipo de Socia',
      icon: 'fas fa-id-badge',
      hasStats: true,
      stats: { total: 0 },
      statsClass: 'card-stats-total-only',
      route: '/tipo-socia'
    },
    {
      id: 'characteristics',
      title: 'Característica Del Síndrome',
      icon: 'fas fa-dna',
      hasStats: false,
      description: 'Administra las características médicas del Síndrome de Turner',
      route: '/syndrome-characteristics',
      handler: () => navigate('/syndrome-characteristics')
    },
    {
      id: 'mentors',
      title: 'Mentores',
      icon: 'fas fa-chalkboard-teacher',
      hasStats: true,
      stats: { total: 0, active: 0, inactive: 0 },
      statsLabels: { active: 'Activos', inactive: 'Inactivos' },
      route: '/mentors',
      createRoute: '/mentors/create',
      createButtonText: 'Crear Mentor'
    },
    {
      id: 'medicos',
      title: 'Médico',
      icon: 'fas fa-stethoscope',
      hasStats: true,
      stats: { total: 0, active: 0, inactive: 0 },
      statsLabels: { active: 'Activos', inactive: 'Inactivos' },
      route: '/medicos',
      createRoute: '/medicos/create',
      createButtonText: 'Crear Médico'
    },
    {
      id: 'hospitals',
      title: 'Hospitales',
      icon: 'fas fa-hospital',
      hasStats: true,
      stats: { total: 0 },
      statsClass: 'card-stats-total-only',
      route: '/hospitals',
      createRoute: '/hospitals/create',
      createButtonText: 'Crear Hospital'
    },
    {
      id: 'inventory',
      title: 'Inventario',
      icon: 'fas fa-boxes',
      hasStats: false,
      description: 'Gestiona recursos médicos, materiales y suministros para el cuidado del Síndrome de Turner',
      route: '/inventory',
      createRoute: '/inventory/create',
      primaryButtonText: 'Ver Inventario',
      createButtonText: 'Agregar Item',
      additionalButtons: [
        {
          text: 'Agregar Ubicación',
          route: '/inventory/location',
          icon: 'fas fa-map-marker-alt'
        },
        {
          text: 'Agregar Categoría',
          route: '/inventory/category',
          icon: 'fas fa-tags'
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reportes',
      icon: 'fas fa-chart-bar',
      hasStats: false,
      description: 'Genera reportes médicos y estadísticas sobre pacientes con Síndrome de Turner',
      route: '/reports',
      createRoute: '/reports/create',
      primaryButtonText: 'Ver Reportes',
      createButtonText: 'Generar Reporte'
    },
    // Módulo para Editar Formatos (visibilidad controlada por MODULE_PERMISSIONS)
    {
      id: 'editar-formatos',
      title: 'Editar Formatos',
      icon: 'fas fa-file-contract',
      hasStats: false,
      description: 'Editar formatos de Carnet y Solicitud de Ingreso',
      route: '/editar-formatos',
      primaryButtonText: 'Ir a Editar',
      handler: () => navigate('/editar-formatos')
    },
    {
      id: 'tratamientos',
      title: 'Gestión de Tratamientos',
      icon: 'fas fa-prescription-bottle-alt',
      hasStats: true,
      stats: tratamientosStats,
      statsLabels: { active: 'Activos', inactive: 'Finalizados' },
      description: 'Administra los tratamientos médicos y seguimientos de las socias',
      route: '/tratamientos',
      primaryButtonText: 'Ver Tratamientos',
      handler: () => navigate('/tratamientos')
    }
  ]);

  // Actualizar el número de tipos de socia desde la API
  useEffect(() => {
    const fetchTiposSocia = async () => {
      try {
        // Verificar permiso antes de cargar
        const userPermissions = getCurrentUserPermissions();
        if (!hasPermission(userPermissions, 'CRUD Tipo Socia') && !hasPermission(userPermissions, 'Administrador')) {
          return;
        }
        
        const res = await getTiposSocia();
        setDashboardModules(prevModules => prevModules.map(m =>
          m.id === 'tipo-socia'
            ? { ...m, stats: { ...m.stats, total: Array.isArray(res.data) ? res.data.length : 0 } }
            : m
        ));
      } catch (e) {
        // Si falla, dejar el valor en 0
        setDashboardModules(prevModules => prevModules.map(m =>
          m.id === 'tipo-socia'
            ? { ...m, stats: { ...m.stats, total: 0 } }
            : m
        ));
      }
    };
    fetchTiposSocia();
  }, []);

  // Función para renderizar estadísticas
  const renderStats = (module) => {
    if (!module.hasStats) return null;

    const statsClass = module.statsClass || 'card-stats';

    // Si es el módulo de tipo-socia, mostrar solo el total
    if (module.statsClass === 'card-stats-total-only') {
      return (
        <div className={statsClass}>
          <div className="stat-item">
            <span className="stat-number">{module.stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
      );
    }

    // Para otros módulos, mostrar todas las estadísticas
    return (
      <div className={statsClass}>
        <div className="stat-item">
          <span className="stat-number">{module.stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-item">
          <span className="stat-number active">{module.stats.active}</span>
          <span className="stat-label">{module.statsLabels.active}</span>
        </div>
        <div className="stat-item">
          <span className="stat-number inactive">{module.stats.inactive}</span>
          <span className="stat-label">{module.statsLabels.inactive}</span>
        </div>
      </div>
    );
  };

  // Función para renderizar descripción
  const renderDescription = (module) => {
    if (module.hasStats || !module.description) return null;
    return <p>{module.description}</p>;
  };

  // Función para manejar navegación
  const handleNavigation = (module) => {
    if (module.handler) {
      module.handler();
    } else {
      navigate(module.route);
    }
  };

  // Función para renderizar botones de acción
  const renderActions = (module) => {
    // Si es el módulo de inventario con botones adicionales
    if (module.id === 'inventory' && module.additionalButtons) {
      return (
        <div className="card-actions inventory-actions">
          <button className="card-button primary" onClick={() => handleNavigation(module)}>
            <i className="fas fa-cogs"></i>
            {module.primaryButtonText || 'Gestionar'}
          </button>
          {module.additionalButtons.map((button, index) => (
            <button 
              key={index} 
              className="card-button additional" 
              onClick={() => navigate(button.route)}
            >
              <i className={button.icon}></i>
              {button.text}
            </button>
          ))}
        </div>
      );
    }
    
    // Para otros módulos, renderizado normal
    return (
      <div className="card-actions">
        <button className="card-button primary" onClick={() => handleNavigation(module)}>
          <i className="fas fa-cogs"></i>
          {module.primaryButtonText || 'Gestionar'}
        </button>
        {module.createRoute && (
          <button className="card-button secondary" onClick={() => navigate(module.createRoute)}>
            <i className="fas fa-plus"></i>
            {module.createButtonText || `Crear ${module.title}`}
          </button>
        )}
      </div>
    );
  };

  // Filtrar módulos según permisos del usuario
  const visibleModules = useMemo(() => {
    const userPermissions = getCurrentUserPermissions();
    
    // Si es administrador, mostrar todos los módulos
    if (hasPermission(userPermissions, 'Administrador')) {
      return dashboardModules;
    }
    
    // Filtrar módulos según permisos
    return dashboardModules.filter(module => {
      const requiredPermission = MODULE_PERMISSIONS[module.id];
      
      // Si no tiene permiso requerido configurado, no mostrar
      if (!requiredPermission) {
        return false;
      }
      
      return hasPermission(userPermissions, requiredPermission);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardModules]); // Recalcular si cambian los módulos

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="welcome-section">
          <h1>Bienvenido al Sistema FEPAST</h1>
        </div>
        
        <div className="dashboard-grid">
          {visibleModules.map(module => {
            // Para la tarjeta de requisitos-socia, aplicar clases avanzadas igual que las otras
            const advancedClass =
              module.id === 'requisitos-socia'
                ? 'users-card'
              : module.id === 'especialidades-medicas'
                ? 'professions-card' // Usa los mismos estilos avanzados que profesiones
                : '';
            return (
              <div key={module.id} className={`dashboard-card ${module.id}-card ${advancedClass}`}>
                <div className="card-icon">
                  <i className={module.icon}></i>
                </div>
                <h3>{module.title}</h3>
                {renderStats(module)}
                {renderDescription(module)}
                {renderActions(module)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;